/**
 * Utility functions for determining market type and display mode
 */

import constants from '@/constants'
import { getPositionLabels } from '@/features/market/utils/positionLabels'
import { isSportsEvent } from '@/features/market/utils/sportsEvent'

/**
 * Two-outcome markets typed as yesno or updown on both sides.
 * Kept for callers that still inspect payload shape; UI classification no longer
 * treats this as a special "binary outcomes" display mode (linked matches independent).
 *
 * @param {Object} question
 * @returns {boolean}
 */
export const isBinaryYesNoUpdownOutcomes = question => {
  if (!question?.outcomes || question.outcomes.length !== 2) return false
  // Only treat as "binary outcomes" when BOTH outcomes indicate a binary label style.
  // This avoids misclassifying independent multi-outcome markets that happen to have a bad/mismatched position_labels on outcome[0].
  const a = question.outcomes?.[0]?.position_labels
  const b = question.outcomes?.[1]?.position_labels
  const isBinaryA = a === 'yesno' || a === 'updown'
  const isBinaryB = b === 'yesno' || b === 'updown'
  return isBinaryA && isBinaryB && a === b
}

/**
 * Single source of truth for market UI classification.
 * Assumes backend always provides `markets_correlation` (no legacy fallback).
 *
 * UI layout is driven by outcome shape (count + position_labels) only.
 * `markets_correlation` (`linked` | `independent`) does not change display mode,
 * order book placement, or place-bet controls — only the `kind` label prefix.
 *
 * @param {Object} question
 * @returns {{
 *  kind: 'linked_multiple'|'independent_mono_custom'|'independent_mono_binary'|'independent_multi'|'unknown',
 *  displayMode: 'buttons'|'list-no-buttons'|'list-with-buttons',
 *  isMono: boolean,
 *  needsVirtualOutcomes: boolean,
 *  orderBook: { location: 'belowCard'|'inlineAccordion', selectorType: 'tabs'|'dropdown', showTabs: boolean },
 *  placeBet: { outcomeSource: 'selected'|'first', positionSource: 'selected'|'alwaysLong'|'selectedOrDefaultLong' }
 *  binaryLabelStyle: 'yesno'|'updown'|null,
 *  isSportsEvent: boolean
 * }}
 */
export function deriveMarketUi(question) {
  const outcomes = question?.outcomes ?? []
  const outcomesLength = outcomes.length
  const marketExclusivity = question?.markets_correlation
  const firstPositionLabels = outcomes?.[0]?.position_labels
  // Yes/No & Up/Down button semantics apply only to mono markets (one physical outcome).
  const monoBinaryLabelStyle =
    outcomesLength === 1 && (firstPositionLabels === 'yesno' || firstPositionLabels === 'updown')
      ? firstPositionLabels
      : null
  const binaryLabelStyle = monoBinaryLabelStyle
  const multiKind = marketExclusivity === 'linked' ? 'linked_multiple' : 'independent_multi'

  const base = {
    kind: 'unknown',
    displayMode: 'list-no-buttons',
    isMono: false,
    needsVirtualOutcomes: false,
    binaryLabelStyle,
    isSportsEvent: isSportsEvent(question),
    orderBook: {
      location: 'inlineAccordion',
      selectorType: 'dropdown',
      showTabs: false,
    },
    placeBet: {
      outcomeSource: 'selected',
      positionSource: 'selectedOrDefaultLong',
    },
  }

  if (!question || outcomesLength === 0) return base

  // Mono = one physical outcome, positions represent LONG/SHORT
  if (outcomesLength === 1) {
    const isCustomPositions = firstPositionLabels === 'custom'
    const isBinaryPositions = firstPositionLabels === 'yesno' || firstPositionLabels === 'updown'

    if (isCustomPositions) {
      return {
        ...base,
        kind: 'independent_mono_custom',
        displayMode: 'list-no-buttons',
        isMono: true,
        needsVirtualOutcomes: true,
        // Mono-custom should not show selector tabs
        orderBook: { ...base.orderBook, location: 'inlineAccordion', selectorType: 'dropdown', showTabs: false },
        placeBet: { outcomeSource: 'first', positionSource: 'selectedOrDefaultLong' },
      }
    }

    if (isBinaryPositions) {
      return {
        ...base,
        kind: 'independent_mono_binary',
        displayMode: 'buttons',
        isMono: true,
        needsVirtualOutcomes: false,
        // Mono yes/no & up/down should use tabs where OrderBook selector is shown
        orderBook: { location: 'belowCard', selectorType: 'tabs', showTabs: true },
        placeBet: { outcomeSource: 'first', positionSource: 'selectedOrDefaultLong' },
      }
    }

    // If backend sends a single outcome but doesn't specify labels, treat it as binary positions.
    return {
      ...base,
      kind: 'independent_mono_binary',
      displayMode: 'buttons',
      isMono: true,
      needsVirtualOutcomes: false,
      orderBook: { location: 'belowCard', selectorType: 'tabs', showTabs: true },
      placeBet: { outcomeSource: 'first', positionSource: 'selectedOrDefaultLong' },
    }
  }

  // Multi-outcome: same list + Yes/No (or custom) position UI for linked and independent
  if (outcomesLength > 1) {
    return {
      ...base,
      kind: multiKind,
      displayMode: 'list-with-buttons',
      isMono: false,
      needsVirtualOutcomes: false,
      orderBook: { ...base.orderBook, location: 'inlineAccordion', selectorType: 'dropdown', showTabs: false },
      placeBet: { outcomeSource: 'selected', positionSource: 'selectedOrDefaultLong' },
    }
  }

  return base
}

/**
 * Image for one side of a mono market. The position upload wins; the shared
 * market thumbnail remains when that side has no image of its own.
 */
function thumbnailForPosition(outcome, position) {
  const specific =
    position === constants.position.LONG ? outcome?.long_thumbnail : outcome?.short_thumbnail
  return specific || outcome?.thumbnail || null
}

/**
 * For independent mono markets, we can display TWO “virtual outcomes”
 * that map to the same physical outcome + a position (LONG/SHORT).
 * Primarily used for custom position labels in the list UI.
 */
export function getVirtualOutcomesForMonoCustom(question) {
  const ui = deriveMarketUi(question)
  if (ui.kind !== 'independent_mono_custom') {
    return { actualOutcome: null, virtualOutcomes: [] }
  }

  const actualOutcome = question?.outcomes?.[0]
  if (!actualOutcome) return { actualOutcome: null, virtualOutcomes: [] }

  const labels = getPositionLabels(actualOutcome, question)

  const longPrice = {}
  const shortPrice = {}
  Object.keys(actualOutcome.price || {}).forEach(currencyId => {
    const p = actualOutcome.price?.[currencyId] ?? 0
    longPrice[currencyId] = p
    shortPrice[currencyId] = 1 - p
  })

  return {
    actualOutcome,
    virtualOutcomes: [
      {
        id: actualOutcome.id,
        virtualId: `${actualOutcome.id}_long`,
        title: labels.longLabel,
        position: constants.position.LONG,
        price: longPrice,
        thumbnail: thumbnailForPosition(actualOutcome, constants.position.LONG),
      },
      {
        id: actualOutcome.id,
        virtualId: `${actualOutcome.id}_short`,
        title: labels.shortLabel,
        position: constants.position.SHORT,
        price: shortPrice,
        thumbnail: thumbnailForPosition(actualOutcome, constants.position.SHORT),
      },
    ],
  }
}

/**
 * Determines if a market uses buttons display mode
 * Button markets are mono binary (one outcome, yesno/updown position labels).
 *
 * @param {Object} question - The question/market object
 * @returns {boolean} True if market uses buttons display, false otherwise
 */
export const usesButtonsDisplay = (question) => {
  const ui = deriveMarketUi(question)
  return ui.displayMode === 'buttons'
}

/**
 * Determines the display mode for PlaceBet outcome selector.
 * Returns 'buttons' (two buttons), 'list-no-buttons' (dropdown only), or 'list-with-buttons' (dropdown + position buttons).
 * Used by BuyPlaceBet and PlaceBet skeleton to render the correct layout from question data.
 *
 * @param {Object} question - The question/market object (from server/preload)
 * @returns {'buttons'|'list-no-buttons'|'list-with-buttons'}
 */
export const getDisplayMode = (question) => {
  return deriveMarketUi(question).displayMode
}

/**
 * Independent mono-custom markets have one physical outcome but two complementary custom labels
 * (e.g. "Plessis" vs "Usman"), unlike Yes/No where the complement is implicit. The backend only
 * returns one price series for mono outcomes, so synthesize the complementary series client-side.
 *
 * @param {Array} rawPrices - price history series as the endpoint returns them
 * @param {Object} question - The question/market object
 * @returns {Array} the series to draw, unchanged for every other market type
 */
export function withMonoCustomComplement(rawPrices, question) {
  const outcome = question?.outcomes?.[0]
  if (deriveMarketUi(question).kind !== 'independent_mono_custom' || !rawPrices || rawPrices.length !== 1 || !outcome) {
    return rawPrices
  }

  const labels = getPositionLabels(outcome, question)
  const [longSeries] = rawPrices

  return [
    { ...longSeries, name: labels.longLabel, outcome_id: outcome.id, virtualPosition: 'long' },
    {
      ...longSeries,
      name: labels.shortLabel,
      outcome_id: outcome.id,
      virtualPosition: 'short',
      data: (longSeries.data || []).map(point => ({ ...point, y: Math.round((100 - point.y) * 10) / 10 })),
    },
  ]
}
